import { hostname } from 'os';
import {
  JobsClient,
  subscribe,
  type DispatchedJob,
  type JobResult,
  type PoolRef,
  type Subscription,
} from '@whittlelabs/jobs';
import { performSelfUpdate, readUpgradeDirective } from '../lifecycle/self-update';
import {
  shutdownOnDecommission,
  readDecommissionDirective,
} from '../lifecycle/decommission';
import { readShuttleStrategy } from '@whittlelabs/loom';
import {
  ObserverChain,
  SpendCapExceeded,
  type LifecycleEvent,
  type LifecycleObserver,
} from '../observers/chain';
import type { ExecutionContext, JobExecutor } from '../executors/types';
import { AgentTokenManager } from '@whittlelabs/keep';
import type { BrandConfig } from '../branding/types';
import { resolvePaths, DEFAULT_PROFILE } from '../branding/apply';
import type { ShuttleConfig } from '../config/schema';
import { checkResponseMinVersion } from '../version-check';
import { createLogger, setGlobalLogger, getLogger } from '../logging/logger';
import { ExecutorRegistry } from '../executors/registry';
import { createClaudeCodeExecutor } from '../executors/claude-code';
import { createHttpApiExecutor } from '../executors/http-api';
import { createAnthropicApiExecutor } from '../executors/anthropic-api';
import { createWebhookExecutor } from '../executors/webhook';
import { createCustomScriptExecutor } from '../executors/custom-script';
import { createLoggerObserver } from '../observers/logger';
import { createAuditLogObserver } from '../observers/audit-log';
import {
  createSpendTrackerObserver,
  type SpendTrackerHandle,
} from '../observers/spend-tracker';
import { PairingConfigStore } from '../pairing/store';

export interface ShuttleOptions {
  brand: BrandConfig;
  config: ShuttleConfig;
  /** Active config profile; selects which `~/.<brand>/<profile>/` to read. */
  profile?: string;
}

/**
 * Top-level orchestrator. Constructed per `<brand> run` invocation.
 *
 * Composition:
 *   1. Load pairing credentials (or fall back to apiKey from config).
 *   2. Build the ExecutorRegistry under the brand's allowlist.
 *   3. Compose the ObserverChain: logger → audit-log → spend-tracker → brand observers.
 *   4. Call `@whittlelabs/jobs`' `subscribe()` with the assembled pieces.
 *
 * `subscribe()` owns the dispatch lifecycle; the substrate composes
 * trust controls via observers and stays out of the claim/execute/submit
 * path.
 */
export class Shuttle {
  private readonly brand: BrandConfig;
  private readonly config: ShuttleConfig;
  private readonly profile: string;
  private subscription: Subscription | null = null;
  private spendTracker: SpendTrackerHandle | null = null;

  constructor(options: ShuttleOptions) {
    this.brand = options.brand;
    this.config = options.config;
    this.profile = options.profile ?? DEFAULT_PROFILE;
  }

  /** Exposed so the CLI's `caps set` can reload after a write. */
  get spendTrackerHandle(): SpendTrackerHandle | null {
    return this.spendTracker;
  }

  async start(): Promise<void> {
    setGlobalLogger(
      createLogger({
        level: this.config.logging.level,
        format: this.config.logging.format,
      }),
    );

    const logger = getLogger();
    logger.info(`${this.brand.product.title} starting...`);

    const paths = resolvePaths(this.brand, this.profile);

    // Min-version enforcement seam. Every Keep + Jobs response carries
    // X-Sifter-Min-Version; if the local build is below the floor the
    // check throws UpgradeRequiredError, which bin.ts surfaces to the
    // user with an "npm install -g <package>@latest" hint. Covers both
    // backends: Keep via ServiceTokenManager's exchange call (token
    // refresh), Jobs via the subscribe loop's claim/submit responses.
    const enforceMinVersion = (response: Pick<Response, 'headers'>): void => {
      checkResponseMinVersion({
        response,
        currentVersion: this.brand.product.version,
        packageName: this.brand.product.packageName,
      });
    };

    // 1. Resolve credentials. Pairing config takes precedence; the
    // legacy apiKey path stays for development.
    const auth = await resolveAuth(this.brand, this.config, paths.configFile, enforceMinVersion);

    const jobsClient = new JobsClient({
      baseUrl: this.config.auth.jobsApiUrl,
      apiKey: auth.kind === 'apiKey' ? auth.apiKey : undefined,
      getAccessToken: auth.kind === 'tokenProvider' ? auth.getToken : undefined,
      onResponse: enforceMinVersion,
    });

    // 2. ExecutorRegistry under the brand's allowlist; register only the
    // executors the brand permits, and only those with config rows.
    const registry = new ExecutorRegistry(this.brand.executorAllowlist);
    registerBuiltInExecutors(registry, this.brand.executorAllowlist);

    const executors = registry.build(
      mergeExecutorDefaults(
        this.brand.executorDefaults,
        this.config.executors as Record<string, Record<string, unknown>>,
      ),
    );

    // Map each shuttle strategy to the executor that handles it (ADR 0005).
    const byStrategy = new Map<string, JobExecutor>();
    for (const executor of executors) {
      for (const strategy of executor.strategies) byStrategy.set(strategy, executor);
    }

    // 3. Compose the observer chain. Trust controls first; brand
    //    observers run last, after spend-tracker.
    this.spendTracker = createSpendTrackerObserver(this.brand, paths.spendStateFile);
    const observers: LifecycleObserver[] = [
      createLoggerObserver(),
      createAuditLogObserver(this.brand, paths.auditFile),
      this.spendTracker,
      ...(this.brand.observers ?? []),
    ];
    const observerChain = new ObserverChain(observers);

    const onObserverError = (err: unknown, observerName: string, event: LifecycleEvent): void => {
      logger.warn('observer threw on non-veto event', {
        observerName,
        eventKind: event.kind,
        error: err instanceof Error ? err.message : String(err),
      });
    };

    // 4. The Shuttle's onJob callback: demux by strategy, run the observer chain
    //    with the two-phase spend veto, execute, return outputs (ADR 0005). The
    //    generic claim loop submits those outputs and closes attendance.
    const onJob = async (dispatch: DispatchedJob, signal: AbortSignal): Promise<JobResult> => {
      const jobId = dispatch.job.id;
      const attendanceId = dispatch.attendanceId;
      const startedAt = Date.now();
      const strategy = readShuttleStrategy(dispatch.inputs) ?? '';
      const executor = byStrategy.get(strategy);
      if (!executor) {
        const reason = `no executor for shuttle strategy "${strategy}"`;
        await observerChain.notify(
          { kind: 'attendance.closed', jobId, attendanceId, status: 'failed', reason, totalDurationMs: Date.now() - startedAt },
          onObserverError,
        );
        return { status: 'failed', reason };
      }

      await observerChain.notify(
        {
          kind: 'job.claimed',
          jobId,
          attendanceId,
          attempt: dispatch.job.attempt,
          deadline: dispatch.deadline,
          claimedAt: dispatch.claimedAt,
          dispatch,
        },
        onObserverError,
      );

      // Two-phase spend veto: estimate (cheap, no render) → preflight → execute.
      let estimate: { inputTokens?: number; outputTokens?: number; costUsd?: number } = {};
      if (executor.estimate) {
        try {
          estimate = await executor.estimate(dispatch);
        } catch {
          // estimate is best-effort; fall through with no estimate.
        }
      }
      try {
        await observerChain.notify({ kind: 'spend.preflight', jobId, attendanceId, estimate });
      } catch (err) {
        const reason = err instanceof SpendCapExceeded
          ? 'refused_over_cap'
          : `preflight rejected: ${err instanceof Error ? err.message : String(err)}`;
        await observerChain.notify(
          { kind: 'attendance.closed', jobId, attendanceId, status: 'failed', reason, totalDurationMs: Date.now() - startedAt },
          onObserverError,
        );
        return { status: 'failed', reason };
      }

      await observerChain.notify(
        { kind: 'executor.dispatch.started', jobId, attendanceId, executor: executor.capability.id, strategy },
        onObserverError,
      );
      const dispatchStartedAt = Date.now();
      // Live-progress sink, bound to this job. The executor decides whether to
      // use it (only when the job carries a progressChannel); products that
      // don't opt in generate zero progress traffic. Fire-and-forget: a failed
      // progress POST never touches the job's outcome.
      const executionCtx: ExecutionContext = {
        reportProgress: (channel, frames) => {
          void jobsClient.postProgress(jobId, { channel, frames }).catch(() => {});
        },
      };
      let response;
      try {
        response = await executor.execute(dispatch, signal, executionCtx);
      } catch (err) {
        const reason = `executor failed: ${err instanceof Error ? err.message : String(err)}`;
        await observerChain.notify(
          { kind: 'attendance.closed', jobId, attendanceId, status: 'failed', reason, totalDurationMs: Date.now() - startedAt },
          onObserverError,
        );
        return { status: 'failed', reason };
      }
      await observerChain.notify(
        {
          kind: 'executor.dispatch.completed',
          jobId,
          attendanceId,
          executor: executor.capability.id,
          response: { rawText: response.rawText ?? '', parsed: response.parsed, usage: response.usage },
          durationMs: Date.now() - dispatchStartedAt,
        },
        onObserverError,
      );

      const outputs = [...response.outputs];
      if (response.usage) {
        outputs.push({ label: 'usage', content: response.usage as Record<string, unknown> });
      }
      for (const output of outputs) {
        await observerChain.notify(
          { kind: 'output.submitted', jobId, attendanceId, label: output.label, content: output.content },
          onObserverError,
        );
      }
      await observerChain.notify(
        { kind: 'attendance.closed', jobId, attendanceId, status: 'completed', totalDurationMs: Date.now() - startedAt },
        onObserverError,
      );
      return { status: 'completed', outputs };
    };

    // 5. Build pool refs and start the generic claim loop.
    const pools: PoolRef[] = this.config.pools.map((p) =>
      p.id ? { id: p.id } : { name: p.name as string },
    );

    const subscribeLogger = {
      debug: (msg: string, meta?: Record<string, unknown>) => logger.debug(msg, meta),
      info: (msg: string, meta?: Record<string, unknown>) => logger.info(msg, meta),
      warn: (msg: string, meta?: Record<string, unknown>) => logger.warn(msg, meta),
      error: (msg: string, meta?: Record<string, unknown>) => logger.error(msg, meta),
    };

    // Failed installs are attempted once per requested version, not on
    // every 2s poll; a newer request resets the gate.
    const attemptedUpgrades = new Set<string>();

    this.subscription = subscribe({
      jobsClient,
      pools,
      subscriber: {
        identityType: 'agent',
        identityId: this.config.agent.identityId,
        intelligence: 'digital',
        displayName: this.config.agent.displayName,
        // Self-reported build facts, refreshed on every registration.
        // Products read these off the pool-member row (Sift's settings
        // page shows the version and offers the upgrade action).
        metadata: {
          ...this.config.agent.metadata,
          client_version: this.brand.product.version,
          hostname: hostname(),
        },
      },
      onJob,
      onDirectives: async (directives) => {
        // Decommission outranks everything: the credential is already
        // revoked product-side; the only right move is a graceful exit.
        const decommission = readDecommissionDirective(directives);
        if (decommission) {
          await shutdownOnDecommission({
            productTitle: this.brand.product.title,
            directive: decommission,
            logger: subscribeLogger,
            hooks: {
              stopLoop: () => this.subscription?.stop() ?? Promise.resolve(),
            },
          });
          return;
        }
        const directive = readUpgradeDirective(
          directives,
          this.brand.product.version,
          attemptedUpgrades,
        );
        if (!directive) return;
        attemptedUpgrades.add(directive.requested_version);
        await performSelfUpdate({
          packageName: this.brand.product.packageName,
          directive,
          logger: subscribeLogger,
          hooks: {
            stopLoop: () => this.subscription?.stop() ?? Promise.resolve(),
            restartLoop: () => this.subscription?.start() ?? Promise.resolve(),
          },
        });
      },
      logger: subscribeLogger,
      pollIntervalMs: this.config.polling.intervalMs,
      maxConcurrency: this.config.concurrency.maxJobs,
    });

    await this.subscription.start();

    logger.info(`${this.brand.product.title} running`, {
      profile: this.profile,
      pools: pools.length,
      executors: executors.map((e) => e.capability.id),
      maxConcurrency: this.config.concurrency.maxJobs,
      observers: observers.length,
    });
  }

  async stop(): Promise<void> {
    const logger = getLogger();
    logger.info(`${this.brand.product.title} stopping...`);
    if (this.subscription) {
      await this.subscription.stop();
    }
    logger.info(`${this.brand.product.title} stopped`);
  }
}

/**
 * Brand executor defaults, merged UNDER the user's config: per executor,
 * a user-set key always wins; brand keys fill only the gaps. Executors
 * the user never configured are NOT conjured from defaults — a defaults
 * fragment tunes what the user enabled, it doesn't enable anything.
 */
export function mergeExecutorDefaults(
  defaults: Record<string, Record<string, unknown>> | undefined,
  userExecutors: Record<string, Record<string, unknown>>,
): Record<string, Record<string, unknown>> {
  if (!defaults) return userExecutors;
  const merged: Record<string, Record<string, unknown>> = {};
  for (const [name, userConfig] of Object.entries(userExecutors)) {
    merged[name] = { ...(defaults[name] ?? {}), ...userConfig };
  }
  return merged;
}

function registerBuiltInExecutors(
  registry: ExecutorRegistry,
  allowlist: ReadonlyArray<string>,
): void {
  const all: Array<[string, (config: Record<string, unknown>, capabilityId?: string) => JobExecutor]> = [
    ['claude-code', createClaudeCodeExecutor],
    ['http-api', createHttpApiExecutor],
    ['anthropic-api', createAnthropicApiExecutor],
    ['webhook', createWebhookExecutor],
    ['custom-script', createCustomScriptExecutor],
  ];
  const allowAll = allowlist[0] === '*';
  for (const [name, factory] of all) {
    if (allowAll || allowlist.includes(name)) {
      registry.register(name, factory);
    }
  }
}

type ResolvedAuth =
  | { kind: 'apiKey'; apiKey: string }
  | { kind: 'tokenProvider'; getToken: (opts?: { forceRefresh?: boolean }) => Promise<string | null> };

async function resolveAuth(
  brand: BrandConfig,
  config: ShuttleConfig,
  pairingFile: string,
  onResponse: (response: Pick<Response, 'headers' | 'status'>) => void,
): Promise<ResolvedAuth> {
  // Prefer agent-token auth when paired credentials exist. The
  // Sifter-style pairing flow writes credentials into the `agents`
  // table; AgentTokenManager exchanges them at /api/agent-tokens.
  // Service identities (daemon-style) use ServiceTokenManager via
  // a different code path.
  const store = new PairingConfigStore(pairingFile);
  if (store.exists()) {
    const pairing = store.read();
    const tokens = new AgentTokenManager({
      keepApiUrl: pairing.keepApiUrl,
      clientId: pairing.credentials.clientId,
      clientSecret: pairing.credentials.clientSecret,
      onResponse,
    });
    // Validate the stored credentials mint a token now (fail fast), then
    // hand the JobsClient the manager's getToken so every request gets a
    // fresh-enough token. The manager caches and re-mints ~30s before
    // expiry, so a long-lived subscribe loop never serves a stale token
    // (the bug where the daemon 401'd forever once the first token aged out).
    const token = await tokens.getToken();
    if (token) {
      return {
        kind: 'tokenProvider',
        getToken: (opts) => tokens.getToken(opts?.forceRefresh),
      };
    }
  }

  // Fall back to apiKey from config (dev path).
  if (config.auth.apiKey) {
    return { kind: 'apiKey', apiKey: config.auth.apiKey };
  }

  throw new Error(
    `No credentials available. Run \`${brand.product.cliBinary} init\` to pair, or set auth.apiKey in your config.`,
  );
}
